import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Switch,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  Avatar,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import type {
  AlbumResponse,
  AlbumCreate,
  AlbumUpdate,
} from "../../api/generated/types.gen";
import {
  cancelButtonStyle,
  primaryButtonStyle,
} from "../../styles/buttonStyles";
import { useImages, Image } from "../../hooks/useImages";
import { getImageId } from "../../utils/imageUtils";
import { ImageCropModal } from "./ImageCropModal";

// Alias for convenience
type Album = AlbumResponse;

interface GalleryFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AlbumCreate | AlbumUpdate) => Promise<void>;
  album?: Album | null;
  isLoading?: boolean;
  error?: string | null;
}

export const GalleryForm: React.FC<GalleryFormProps> = ({
  open,
  onClose,
  onSubmit,
  album,
  isLoading = false,
  error = null,
}) => {
  const { data: imagesData } = useImages({ page_size: 100 });
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    cover_image_id: "",
    is_public: true,
    is_featured: false,
    image_ids: [] as string[],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImageForCrop, setSelectedImageForCrop] =
    useState<Image | null>(null);

  // Load album images when editing
  useEffect(() => {
    const loadAlbumImages = async () => {
      if (open && album?.id) {
        try {
          const { getPublicAlbumImagesApiPublicAlbumsAlbumIdImagesGet } =
            await import("../../api/generated");
          const imagesResponse =
            await getPublicAlbumImagesApiPublicAlbumsAlbumIdImagesGet({
              path: { album_id: album.id },
            });

          if (imagesResponse.data) {
            const coverImageId = album.cover_image_id;
            // Filter out the cover image from image_ids since it's stored separately
            const imageIds = (imagesResponse.data.items || [])
              .map((img: any) => img.id || img.cloudinary_public_id)
              .filter((id: string) => id && id !== coverImageId); // Exclude cover image
            setFormData((prev) => ({
              ...prev,
              image_ids: imageIds,
            }));
          }
        } catch (error) {
          console.error("Failed to load album images:", error);
        }
      }
    };

    loadAlbumImages();
  }, [open, album?.id, album?.cover_image_id]);

  // Reset form when dialog opens/closes or album changes
  useEffect(() => {
    if (open) {
      if (album) {
        setFormData((prev) => ({
          ...prev,
          title: album.title,
          description: album.description || "",
          cover_image_id: album.cover_image_id || "",
          is_public: album.is_public ?? true,
          is_featured: album.is_featured ?? false,
          // image_ids will be loaded by the other useEffect
        }));
      } else {
        setFormData({
          title: "",
          description: "",
          cover_image_id: "",
          is_public: true,
          is_featured: false,
          image_ids: [],
        });
      }
      setErrors({});
    }
  }, [open, album]);

  const handleInputChange =
    (field: string) =>
    (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      const target = event.target as HTMLInputElement;
      const value = target.type === "checkbox" ? target.checked : target.value;

      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    };

  const handleSelectChange =
    (field: string) => (event: { target: { value: unknown } }) => {
      const value = event.target.value as string;

      // If selecting cover image, open crop modal instead of directly setting
      if (field === "cover_image_id" && value) {
        const selectedImage = availableImages.find(
          (img) => getImageId(img) === value
        );
        if (selectedImage) {
          setSelectedImageForCrop(selectedImage);
          setCropModalOpen(true);
          return; // Don't set the value yet, wait for crop completion
        }
      }

      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error when user changes selection
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    };

  const handleCropComplete = (croppedImageId: string) => {
    setFormData((prev) => ({
      ...prev,
      cover_image_id: croppedImageId,
    }));
    setCropModalOpen(false);
    setSelectedImageForCrop(null);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // If no cover image is selected, use the first image in the gallery
      let coverImageId = formData.cover_image_id;
      if (!coverImageId && formData.image_ids.length > 0) {
        coverImageId = formData.image_ids[0];
      }

      // Ensure cover image is not included in image_ids
      const imageIdsWithoutCover = formData.image_ids.filter(
        (id) => id !== coverImageId
      );

      const submitData: AlbumCreate | AlbumUpdate = {
        title: formData.title,
        description: formData.description || undefined,
        cover_image_id: coverImageId || undefined,
        is_public: formData.is_public,
        is_featured: formData.is_featured,
        // Always include image_ids: empty array clears images, non-empty sets images
        // Make sure cover image is excluded
        image_ids: imageIdsWithoutCover.length > 0 ? imageIdsWithoutCover : [],
      };

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      // Error handling is done by the parent component
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const availableImages = imagesData?.items || [];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false} // Full width on mobile
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          overflow: "hidden", // Prevent scrollbar outside modal
          width: { xs: "100%", sm: "600px" }, // Full width on mobile, fixed on desktop
          maxWidth: { xs: "100%", sm: "600px" },
          margin: { xs: 0, sm: "32px" }, // No margin on mobile
          maxHeight: { xs: "100vh", sm: "90vh" }, // Full height on mobile
          height: { xs: "100vh", sm: "auto" }, // Full height on mobile
          borderRadius: { xs: 0, sm: "4px" }, // No border radius on mobile
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 3, sm: 4 },
          pt: { xs: 3, sm: 4 },
          pb: { xs: 2, sm: 2 },
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
        }}
      >
        {album ? "Edit Gallery" : "Create New Gallery"}
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent
          sx={{
            px: { xs: 3, sm: 4 },
            pt: { xs: 2, sm: 2 },
            pb: { xs: 2, sm: 2 },
            overflowY: "auto", // Enable scrolling within content
            overflowX: "hidden", // Prevent horizontal scroll
            maxHeight: { xs: "calc(100vh - 240px)", sm: "calc(100vh - 260px)" }, // Reduced height to leave more space for actions
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0, 0, 0, 0.2)",
              borderRadius: "4px",
              "&:hover": {
                background: "rgba(0, 0, 0, 0.3)",
              },
            },
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 3, sm: 2.5 },
            }}
          >
            <TextField
              label="Title"
              value={formData.title}
              onChange={handleInputChange("title")}
              error={!!errors.title}
              helperText={errors.title}
              placeholder="Enter gallery title"
              required
              fullWidth
              disabled={isLoading}
              variant="standard"
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: { xs: "16px", sm: "16px" }, // Prevent zoom on iOS
                  minHeight: { xs: "56px", sm: "48px" }, // Better touch target on mobile
                },
                "& .MuiInputBase-input": {
                  padding: { xs: "16px 14px", sm: "14px 14px" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "16px", sm: "14px" },
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
              }}
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={handleInputChange("description")}
              placeholder="Enter gallery description"
              fullWidth
              disabled={isLoading}
              variant="standard"
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: { xs: "16px", sm: "16px" }, // Prevent zoom on iOS
                  minHeight: { xs: "56px", sm: "48px" }, // Better touch target on mobile
                },
                "& .MuiInputBase-input": {
                  padding: { xs: "16px 14px", sm: "14px 14px" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "16px", sm: "14px" },
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
              }}
            />

            <FormControl
              fullWidth
              variant="standard"
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: { xs: "16px", sm: "16px" }, // Prevent zoom on iOS
                  minHeight: { xs: "56px", sm: "48px" }, // Better touch target on mobile
                },
                "& .MuiInputBase-input": {
                  padding: { xs: "16px 14px", sm: "14px 14px" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "16px", sm: "14px" },
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
              }}
            >
              <InputLabel>Cover Image</InputLabel>
              <Select
                value={formData.cover_image_id}
                onChange={handleSelectChange("cover_image_id")}
                label="Cover Image"
                disabled={isLoading}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {availableImages
                  .filter((img) => img.id || img.cloudinary_public_id)
                  .map((img) => {
                    const imageId = img.id || img.cloudinary_public_id;
                    return (
                      <MenuItem key={imageId} value={imageId || ""}>
                        {img.title || img.cloudinary_public_id}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              variant="standard"
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: { xs: "16px", sm: "16px" },
                  minHeight: { xs: "56px", sm: "48px" },
                },
                "& .MuiInputBase-input": {
                  padding: { xs: "16px 14px", sm: "14px 14px" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "16px", sm: "14px" },
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
              }}
            >
              <InputLabel>Images</InputLabel>
              <Select
                multiple
                value={formData.image_ids}
                onChange={(e) => {
                  const value = e.target.value as string[];
                  setFormData((prev) => ({
                    ...prev,
                    image_ids: value,
                  }));
                }}
                label="Images"
                disabled={isLoading}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => {
                      const image = availableImages.find(
                        (img) => getImageId(img) === value
                      );
                      const thumbnailUrl = image?.cloudinary_url
                        ? image.cloudinary_url.replace(
                            "/upload/",
                            "/upload/w_50,h_50,c_fill/"
                          )
                        : undefined;
                      return (
                        <Box
                          key={value}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {thumbnailUrl && (
                            <Avatar
                              src={thumbnailUrl}
                              alt={image?.title || "Untitled"}
                              sx={{ width: 20, height: 20 }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                            }}
                          >
                            {image?.title || value || "Untitled"}
                          </Typography>
                        </Box>
                      );
                    })}
                    {selected.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No images selected
                      </Typography>
                    )}
                  </Box>
                )}
                sx={{
                  "& .MuiSelect-select": {
                    minHeight: { xs: "56px", sm: "48px" },
                    padding: { xs: "16px 14px", sm: "14px 14px" },
                  },
                }}
              >
                {availableImages
                  .filter((img) => img.id || img.cloudinary_public_id)
                  .map((img) => {
                    const imageId = getImageId(img);
                    // Generate thumbnail URL from cloudinary_url
                    const thumbnailUrl = img.cloudinary_url
                      ? img.cloudinary_url.replace(
                          "/upload/",
                          "/upload/w_100,h_100,c_fill/"
                        )
                      : undefined;
                    return (
                      <MenuItem key={imageId} value={imageId}>
                        <Checkbox
                          checked={formData.image_ids.indexOf(imageId) > -1}
                        />
                        {thumbnailUrl && (
                          <Avatar
                            src={thumbnailUrl}
                            alt={img.title || "Untitled"}
                            sx={{
                              width: 40,
                              height: 40,
                              mr: 1.5,
                              borderRadius: 1,
                            }}
                            variant="rounded"
                          />
                        )}
                        <ListItemText
                          primary={img.title || "Untitled"}
                          secondary={
                            img.cloudinary_public_id
                              ? img.cloudinary_public_id.substring(0, 30) +
                                "..."
                              : ""
                          }
                        />
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 2.5, sm: 2 },
                pt: { xs: 1, sm: 0 },
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_public}
                    onChange={handleInputChange("is_public")}
                    disabled={isLoading}
                    sx={{
                      "& .MuiSwitch-switchBase": {
                        padding: { xs: "9px", sm: "8px" },
                      },
                    }}
                  />
                }
                label={
                  <Box
                    sx={{
                      fontSize: { xs: "16px", sm: "14px" },
                      fontWeight: 500,
                    }}
                  >
                    Public
                  </Box>
                }
                sx={{
                  margin: 0,
                  alignItems: "center",
                  "& .MuiFormControlLabel-label": {
                    marginLeft: { xs: 2, sm: 1.5 },
                  },
                }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_featured}
                    onChange={handleInputChange("is_featured")}
                    disabled={isLoading}
                    sx={{
                      "& .MuiSwitch-switchBase": {
                        padding: { xs: "9px", sm: "8px" },
                      },
                    }}
                  />
                }
                label={
                  <Box
                    sx={{
                      fontSize: { xs: "16px", sm: "14px" },
                      fontWeight: 500,
                    }}
                  >
                    Featured
                  </Box>
                }
                sx={{
                  margin: 0,
                  alignItems: "center",
                  "& .MuiFormControlLabel-label": {
                    marginLeft: { xs: 2, sm: 1.5 },
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            gap: { xs: 2, sm: 3 },
            px: 4,
            pt: 4, // Top padding to create space above buttons
            pb: 3,
            flexDirection: { xs: "column-reverse", sm: "row" }, // Stack buttons on mobile
            borderTop: { xs: "1px solid rgba(0, 0, 0, 0.12)", sm: "none" }, // Subtle border on mobile
            "& .MuiButton-root": {
              minHeight: { xs: "52px", sm: "40px" }, // Better touch target on mobile
              padding: { xs: "14px 24px", sm: "10px 24px" },
              fontSize: { xs: "16px", sm: "14px" },
              width: { xs: "100%", sm: "auto" }, // Full width on mobile
              fontWeight: { xs: 500, sm: 400 },
              borderRadius: { xs: "8px", sm: "4px" },
              border: "none",
              boxShadow: "none",
            },
          }}
        >
          <Button
            onClick={handleClose}
            disabled={isLoading}
            disableRipple
            sx={cancelButtonStyle}
          >
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            loading={isLoading}
            disabled={isLoading}
            sx={primaryButtonStyle}
          >
            {album ? "Update" : "Create"}
          </LoadingButton>
        </DialogActions>
      </form>

      {/* Image Crop Modal */}
      {selectedImageForCrop && (
        <ImageCropModal
          open={cropModalOpen}
          onClose={() => {
            setCropModalOpen(false);
            setSelectedImageForCrop(null);
          }}
          imageUrl={selectedImageForCrop.cloudinary_url}
          imageTitle={selectedImageForCrop.title || "Cover"}
          onCropComplete={handleCropComplete}
        />
      )}
    </Dialog>
  );
};
